import React, { useState, useEffect } from 'react';
import type { MutableRefObject } from 'react';
import { FlipCameraAndroidSharp as FlipCameraAndroidSharpIcon } from '@mui/icons-material';
import FilterCenterFocusIcon from '@mui/icons-material/FilterCenterFocus';
import './MapControlsRoute.css';
import * as L from 'leaflet';
import { DownloadGpxModal } from '../../modals/DownloadGpxModal';

interface MapControlsRouteProps {
  hasRoutePoints: boolean; // Whether any route points exist
  routePointsCount: number; // Number of route points
  isSavingRoute?: boolean; // Whether route is being saved
  hasRoutingError?: boolean; // Whether the last routing attempt failed
  onFitToRoute: () => void; // Function to call when fit to route is clicked
  onDeleteRoute: () => void; // Function to call when route should be deleted
  onUndo: () => void; // Function to call when undo is clicked
  onRedo: () => void; // Function to call when redo is clicked
  onDownload: () => void; // Function to call when route should be saved/downloaded
  onGpxDownload?: () => void; // Function to call when GPX should be downloaded
  onOverwriteSave?: () => void; // Function to call when overwriting existing route
  onResetRoute: () => void; // Function to call when route should be reset to saved version
  onReverseRoute?: () => void; // Function to call when route should be reversed
  canUndo: boolean; // Whether undo is available
  canRedo: boolean; // Whether redo is available
  hasLoadedRoute: boolean; // Whether a route has been loaded from storage
  hasUnsavedChanges: boolean; // Whether the route has unsaved changes
  currentRouteOwnerId?: string | null; // ID of the route owner/creator
  currentUserId?: string | null; // ID of the current logged-in user
  mapRef: MutableRefObject<L.Map | null>; // Reference to the Leaflet map instance
  routeData?: any; // RouteData metrics for GPX modal summary
  bikeProfileName?: string; // Human readable bike profile name
}

const MapControlsRoute: React.FC<MapControlsRouteProps> = ({ hasRoutePoints, routePointsCount, isSavingRoute = false, hasRoutingError = false, onFitToRoute, onDeleteRoute, onUndo, onRedo, onDownload, onGpxDownload, onOverwriteSave, onResetRoute, onReverseRoute, canUndo, canRedo, hasLoadedRoute, hasUnsavedChanges, currentRouteOwnerId = null, currentUserId = null, mapRef: _mapRef, routeData, bikeProfileName }) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showReverseConfirmation, setShowReverseConfirmation] = useState(false);
  const [showGpxModal, setShowGpxModal] = useState(false);
  
  // Handle click events in safety zone to prevent them from reaching the map
  const handleSafetyZoneClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from reaching the map
    e.preventDefault(); // Prevent default behavior
  };

  const handleDeleteClick = () => {
    // Close other confirmations when opening this one
    setShowResetConfirmation(false);
    setShowSaveConfirmation(false);
    setShowReverseConfirmation(false);
    // Toggle confirmation - if already showing, hide it; if not showing, show it
    setShowDeleteConfirmation(!showDeleteConfirmation);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirmation(false);
    onDeleteRoute();
  };

  const handleResetClick = () => {
    // Close other confirmations when opening this one
    setShowDeleteConfirmation(false);
    setShowSaveConfirmation(false);
    setShowReverseConfirmation(false);
    // Toggle confirmation - if already showing, hide it; if not showing, show it
    setShowResetConfirmation(!showResetConfirmation);
  };

  const handleResetConfirm = () => {
    setShowResetConfirmation(false);
    onResetRoute();
  };

  const handleReverseClick = () => {
    // Close other confirmations when opening this one
    setShowDeleteConfirmation(false);
    setShowResetConfirmation(false);
    setShowSaveConfirmation(false);
    // Toggle confirmation - if already showing, hide it; if not showing, show it
    setShowReverseConfirmation(!showReverseConfirmation);
  };

  const handleReverseConfirm = () => {
    setShowReverseConfirmation(false);
    if (onReverseRoute) {
      onReverseRoute();
    }
  };

  const handleSaveClick = () => {

    // If we have a loaded route and unsaved changes, show confirmation for overwrite
    if (hasLoadedRoute && hasUnsavedChanges && onOverwriteSave) {
      // Close other confirmations when opening this one
      setShowDeleteConfirmation(false);
      setShowResetConfirmation(false);
      setShowReverseConfirmation(false);
      const newValue = !showSaveConfirmation;
      setShowSaveConfirmation(newValue); // Toggle behavior restored
    } else {
      // Otherwise, use the regular save modal
      onDownload();
    }
  };

  const handleSaveConfirm = () => {
    setShowSaveConfirmation(false);
    if (onOverwriteSave) {
      onOverwriteSave();
    }
  };

  // Auto-hide confirmations after 5 seconds
  useEffect(() => {
    if (showDeleteConfirmation) {
      const timer = setTimeout(() => {
        setShowDeleteConfirmation(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showDeleteConfirmation]);

  useEffect(() => {
    if (showResetConfirmation) {
      const timer = setTimeout(() => {
        setShowResetConfirmation(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showResetConfirmation]);

  useEffect(() => {
    if (showSaveConfirmation) {
      const timer = setTimeout(() => {
        setShowSaveConfirmation(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showSaveConfirmation]);

  useEffect(() => {
    if (showReverseConfirmation) {
      const timer = setTimeout(() => {
        setShowReverseConfirmation(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showReverseConfirmation]);

  if (!hasRoutePoints && routePointsCount === 0) {
    // No route points at all - no controls needed
    return null;
  }

  if (!hasRoutePoints) {
    return null; // Don't render anything if there are no route points but routePointsCount > 0
  }

  return (
    <>
      {/* Safety zone wrapper around route controls */}
      <div 
        className="map-controls-route-safety-zone"
        onClick={handleSafetyZoneClick}
        onMouseDown={handleSafetyZoneClick}
      >
        <div className="map-controls-route-container">
          <div className="map-controls-route-panel">
          
          <div className="undo-button-container">
            <button 
              className="route-control-button undo-button" 
              onClick={onUndo}
              title="Undo last change"
              disabled={!canUndo}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7v6h6"/>
                <path d="m21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/>
              </svg>
            </button>
          </div>
          
          <div className="redo-button-container">
            <button 
              className="route-control-button redo-button" 
              onClick={onRedo}
              title="Redo last undone change"
              disabled={!canRedo}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 7v6h-6"/>
                <path d="m3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/>
              </svg>
            </button>
          </div>
          
          <div className="fit-button-container">
            <button 
              className="route-control-button fit-to-route-button" 
              onClick={onFitToRoute}
              title="Fit map to points"
              disabled={false} // Always enabled when route points exist
            >
              <FilterCenterFocusIcon sx={{ fontSize: '20px' }} />
            </button>
          </div>
          
          <div className="delete-button-container">
            <button 
              className="route-control-button discard-route-button" 
              onClick={handleDeleteClick}
              title="Clear Map"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff0000" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            
            <div className={`delete-confirmation-row ${showDeleteConfirmation ? 'visible' : ''}`}>
              <span className="delete-confirm-text">
                Clear Map?
              </span>
              <button 
                className="delete-confirm-button"
                onClick={handleDeleteConfirm}
                title="Confirm deletion"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                  <path d="m6 12 4 4 8-8"/>
                </svg>
              </button>
            </div>
          </div>
          
          {hasLoadedRoute && (
            <div className="reset-button-container">
              <button 
                className="route-control-button reset-route-button" 
                onClick={handleResetClick}
                title="Reset to saved route"
                disabled={!hasUnsavedChanges}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6"/>
                  <path d="m1 10 2.803-2.803A9.003 9.003 0 0 1 21 12"/>
                  <path d="M23 20v-6h-6"/>
                  <path d="m23 14-2.803 2.803A9.003 9.003 0 0 1 3 12"/>
                </svg>
              </button>
              
              <div className={`reset-confirmation-row ${showResetConfirmation ? 'visible' : ''}`}>
                <span className="reset-confirm-text">
                  Reset to last save?
                </span>
                <button 
                  className="reset-confirm-button"
                  onClick={handleResetConfirm}
                  title="Confirm reset"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                    <path d="m6 12 4 4 8-8"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          {routePointsCount > 1 && (
            <div className="save-button-container">
              <button 
                className="route-control-button save-route-button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSaveClick();
                }}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                title={isSavingRoute ? "Saving route..." : hasRoutingError ? "Cannot save route - routing failed" : (hasLoadedRoute && hasUnsavedChanges ? "Overwrite existing route" : "Save route")}
                disabled={isSavingRoute || hasRoutingError || (!hasUnsavedChanges && hasLoadedRoute && currentRouteOwnerId === currentUserId)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17,21 17,13 7,13 7,21"/>
                  <polyline points="7,3 7,8 15,8"/>
                </svg>
              </button>
              
              {hasLoadedRoute && hasUnsavedChanges && onOverwriteSave && (
                <div className={`save-confirmation-container ${showSaveConfirmation ? 'has-visible-options' : ''}`}>
                  {/* Overwrite option */}
                  <div className={`save-option-row ${showSaveConfirmation ? 'visible' : ''}`}>
                    <span className="save-option-text">
                      Overwrite existing route?
                    </span>
                    <button 
                      className="route-control-button save-option-button save-confirm-button"
                      onClick={handleSaveConfirm}
                      title="Confirm overwrite"
                      disabled={isSavingRoute || hasRoutingError}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <path d="m6 12 4 4 8-8"/>
                      </svg>
                    </button>
                  </div>
                  
                  {/* Save as new option */}
                  <div className={`save-option-row ${showSaveConfirmation ? 'visible' : ''}`}>
                    <span className="save-option-text">
                      Save as new Route
                    </span>
                    <button 
                      className="route-control-button save-option-button save-new-button"
                      onClick={() => {
                        setShowSaveConfirmation(false);
                        onDownload();
                      }}
                      title="Save as new route"
                      disabled={isSavingRoute || hasRoutingError}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                        <path d="M12 5v14"/>
                        <path d="M5 12h14"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {routePointsCount > 1 && onGpxDownload && (
            <div className="download-button-container">
              <button 
                className="route-control-button download-route-button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowGpxModal(true);
                }}
                title={hasRoutingError ? "Cannot download route - routing failed" : "Download as GPX file"}
                disabled={hasRoutingError}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </button>
            </div>
          )}
          
          {routePointsCount > 1 && onReverseRoute && (
            <div className="reverse-button-container">
              <button 
                className="route-control-button reverse-route-button" 
                onClick={handleReverseClick}
                title="Reverse route direction"
                disabled={hasRoutingError}
              >
                <FlipCameraAndroidSharpIcon 
                  sx={{ 
                    width: 20, 
                    height: 20, 
                    color: '#666' 
                  }} 
                />
              </button>
              
              <div className={`reverse-confirmation-row ${showReverseConfirmation ? 'visible' : ''}`}>
                <span className="reverse-confirm-text">
                  Reverse Route?
                </span>
                <button 
                  className="reverse-confirm-button"
                  onClick={handleReverseConfirm}
                  title="Confirm route reversal"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                    <path d="m6 12 4 4 8-8"/>
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          </div>
        </div>
      </div>
      {showGpxModal && onGpxDownload && (
        <DownloadGpxModal 
          open={showGpxModal}
          onClose={() => setShowGpxModal(false)}
          onConfirm={() => {
            onGpxDownload();
          }}
          disabled={hasRoutingError}
          routeData={routeData}
          bikeProfileName={bikeProfileName}
        />
      )}
    </>
  );
};

export default MapControlsRoute;
